import { Close } from '@mui/icons-material'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack } from '@mui/material'
import { ReactNode } from 'react'
import { ButtonAction } from '@omnea-app/frontend/components/layout/dialog/buttonAction'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  bodyTexts: (ReactNode | string)[]
  actions: (ButtonAction & { alignLeft?: boolean })[]
  children?: ReactNode
  disableAutoCloseOnConfirm?: boolean
  // When set, renders an X in the title that calls onClose; the label is the button's aria-label.
  closeButtonLabel?: string
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  bodyTexts,
  actions,
  children,
  disableAutoCloseOnConfirm,
  closeButtonLabel,
}: Props) {
  const handleButtonClick = async (buttonAction: ButtonAction) => {
    if (buttonAction.action === 'confirm') {
      onConfirm()
      if (!disableAutoCloseOnConfirm) {
        onClose()
      }
      return
    }
    if (buttonAction.action === 'cancel') {
      onClose()
      return
    }
    buttonAction.action()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle sx={closeButtonLabel ? { pr: 6 } : undefined}>
        {title}
        {closeButtonLabel && (
          <IconButton
            onClick={onClose}
            size="small"
            aria-label={closeButtonLabel}
            sx={(theme) => ({ position: 'absolute', top: theme.spacing(3), right: theme.spacing(3) })}
          >
            <Close fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={4}>
          {bodyTexts.filter(isDefined).map((bodyText, index) => (
            <DialogContentText key={index}>{bodyText}</DialogContentText>
          ))}
          {children && children}
        </Stack>
      </DialogContent>
      <DialogActions>
        {actions.map((action, index) => (
          <Button
            data-testid={action.dataTestId}
            key={index}
            variant={action.variant}
            color={action.color}
            onClick={() => handleButtonClick(action)}
            disabled={action.disabled}
            loading={action.loading}
            sx={{ ...(action.alignLeft ? { mr: 'auto' } : undefined) }}
          >
            {action.text}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  )
}
